import { useMemo, useState } from 'react';
import { GitCompareArrows, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { adminCreateBeforeAfterPair, adminDeleteBeforeAfterPair, apiErrorMessage } from '@/lib/api';
import { BEFORE_AFTER } from '@/constants/testIds';
import { selectContentClass } from './styles';

const Thumb = ({ image }) => (
  <div className="h-10 w-10 rounded-md overflow-hidden border border-[hsl(var(--lxra-border))] flex-shrink-0">
    {image && <img src={image.image_url} alt="" className="h-full w-full object-cover" />}
  </div>
);

/**
 * Before/after pair editor shown inside the images dialog. `pairs` is owned by
 * the parent (so deleting an image can prune pairs); this component only
 * reports additions/removals.
 */
export const BeforeAfterPairs = ({ slug, images, pairs, onPairsChange }) => {
  const [beforeId, setBeforeId] = useState('');
  const [afterId, setAfterId] = useState('');
  const [caption, setCaption] = useState('');
  const [saving, setSaving] = useState(false);
  const imageById = useMemo(() => Object.fromEntries(images.map((i) => [i.id, i])), [images]);

  const handleAdd = async () => {
    if (!beforeId || !afterId) return;
    if (beforeId === afterId) {
      toast.error('Choose two different photos.');
      return;
    }
    setSaving(true);
    try {
      const pair = await adminCreateBeforeAfterPair(slug, beforeId, afterId, caption);
      onPairsChange([...pairs, pair]);
      setBeforeId('');
      setAfterId('');
      setCaption('');
      toast.success('Before/after pair added.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to add pair.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminDeleteBeforeAfterPair(id);
      onPairsChange(pairs.filter((p) => p.id !== id));
    } catch (err) {
      toast.error('Failed to remove pair.');
    }
  };

  const photoOptions = images.map((img, i) => (
    <SelectItem key={img.id} value={img.id}>Photo {i + 1}</SelectItem>
  ));

  return (
    <div className="mt-6 pt-5 border-t border-[hsl(var(--lxra-border))]" data-testid={BEFORE_AFTER.dialog}>
      <p className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))] mb-3 flex items-center gap-1.5">
        <GitCompareArrows className="h-3.5 w-3.5" /> Before / After Pairs
      </p>
      {pairs.length > 0 && (
        <div className="space-y-2 mb-3">
          {pairs.map((p) => (
            <div key={p.id} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416] p-2">
              <Thumb image={imageById[p.before_image_id]} />
              <span className="text-[hsl(var(--lxra-muted))] text-xs">→</span>
              <Thumb image={imageById[p.after_image_id]} />
              <span className="flex-1 text-xs text-[hsl(var(--lxra-muted))] truncate">{p.caption || 'Untitled pair'}</span>
              <Button size="icon" variant="ghost" onClick={() => handleDelete(p.id)} data-testid={BEFORE_AFTER.deleteButton(p.id)} className="h-7 w-7 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10 flex-shrink-0">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <Select value={beforeId} onValueChange={setBeforeId}>
          <SelectTrigger data-testid={BEFORE_AFTER.beforeSelect} className="bg-[#141416] border-[hsl(var(--lxra-border))] h-9 text-xs">
            <SelectValue placeholder="Before photo" />
          </SelectTrigger>
          <SelectContent className={selectContentClass}>{photoOptions}</SelectContent>
        </Select>
        <Select value={afterId} onValueChange={setAfterId}>
          <SelectTrigger data-testid={BEFORE_AFTER.afterSelect} className="bg-[#141416] border-[hsl(var(--lxra-border))] h-9 text-xs">
            <SelectValue placeholder="After photo" />
          </SelectTrigger>
          <SelectContent className={selectContentClass}>{photoOptions}</SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          data-testid={BEFORE_AFTER.captionInput}
          className="bg-[#141416] border-[hsl(var(--lxra-border))] h-9 text-xs"
        />
        <Button
          onClick={handleAdd}
          disabled={!beforeId || !afterId || saving}
          data-testid={BEFORE_AFTER.addButton}
          className="h-9 px-3 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] flex-shrink-0"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};
